import { findStepByOrigId } from "@/flow/utils/nodeUtils";
import { getLoopEndOrigId } from "@/flow/utils/loopUtils";
import {
  compactOrganizedSpacingByOrder,
  STRUCTURE_HORIZONTAL_GAP,
} from "@/flow/utils/incrementalAddShift";

/**
 * The "Organize Steps" layout engine.
 *
 * Lifted verbatim out of EditVueFlow.vue's organizeSteps() so the editor button and the wizard's
 * flow preview lay steps out through one implementation instead of two that drift. It is a pure
 * function over a step list — it reads `ix`/`iy`, loop membership (`parentLoopOrigId` and the
 * `loopEndOrigId` in uiData) and `routes`, and writes `position` — so it needs no editor state.
 *
 * The rules it applies, in order:
 *  1. Base grid: `x = ix * GRID`, `y = iy * GRID`.
 *  2. Nested loops: a LOOP sharing its parent LOOP's row is pushed down 250px per nesting level,
 *     along with its body. Without this a nested loop's back-edge is drawn on top of its parent's.
 *  3. Router branches: each branch is offset far enough down to clear the previous branch.
 *  4. Horizontal compaction to `STRUCTURE_HORIZONTAL_GAP`, then sub-pixel jitter so VueFlow does
 *     not collapse overlapping handles/edges.
 */
export interface OrganizedLayoutOptions {
  /** Skip step 4's compaction — detached groups are compacted separately by their own pass. */
  applySpacing?: boolean;
  /** Restricts shifting to these steps; unset means every step may move. */
  movableOrigIds?: Set<string>;
}

/** A step's identity, in the order the editor and the canvas agree on. */
export const getOrigKey = (s: any): string =>
  String(s?.origId || s?.uniqueId || s?.id || "");

/** Grid pitch of one row/column on the canvas. */
const GRID = STRUCTURE_HORIZONTAL_GAP;

/** Extra drop applied per level of loop nesting, so back-edges do not overlap. */
const NESTED_LOOP_VERTICAL_OFFSET = 250;

export function applyOrganizedLayout(
  layoutRootSteps: any[],
  options: OrganizedLayoutOptions = {}
): void {
  if (!Array.isArray(layoutRootSteps) || layoutRootSteps.length === 0) {
    return;
  }

  const stepMap = new Map<string, any>();

  const collect = (list: any[]) => {
    list.forEach((s) => {
      stepMap.set(getOrigKey(s), s);
      if (Array.isArray(s?.routes)) {
        // eslint-disable-next-line
        s.routes.forEach((r: any) => collect(r.steps || []));
      }
    });
  };

  collect(layoutRootSteps);

  const loopStartByEndOrigId = new Map<string, any>();
  stepMap.forEach((step) => {
    if (step.type !== "LOOP") return;

    const loopEndOrigId = getLoopEndOrigId(step);
    if (loopEndOrigId) {
      loopStartByEndOrigId.set(loopEndOrigId, step);
    }
  });

  const findParentLoopStart = (step: any) => {
    const parentId =
      step?.parentLoopOrigId ??
      (Array.isArray(step?.parentLoopOrigIds)
        ? String(step.parentLoopOrigIds[step.parentLoopOrigIds.length - 1])
        : null);

    if (!parentId) return null;
    return findStepByOrigId(layoutRootSteps, parentId);
  };

  const loopsToShift = new Set<string>();

  stepMap.forEach((step) => {
    if (step.type !== "LOOP") return;

    const parentLoop = findParentLoopStart(step);
    if (parentLoop?.step && step.iy === parentLoop.step.iy) {
      loopsToShift.add(getOrigKey(step));
    }
  });

  const getAncestorLoopOrigIds = (step: any): string[] => {
    if (Array.isArray(step?.parentLoopOrigIds)) {
      return step.parentLoopOrigIds.map((x: any) => String(x));
    }

    const ids: string[] = [];
    const visited = new Set<string>();
    let cur: any = step;

    while (cur?.parentLoopOrigId) {
      const pid = String(cur.parentLoopOrigId);
      if (visited.has(pid)) break;
      visited.add(pid);
      ids.push(pid);

      const found = findStepByOrigId(layoutRootSteps, pid);
      cur = found?.step;
      if (!cur) break;
    }

    return ids;
  };

  const getLoopShiftCount = (step: any) => {
    const ancestors = getAncestorLoopOrigIds(step);
    let count = 0;
    // eslint-disable-next-line
    for (const id of ancestors) {
      if (loopsToShift.has(id)) count++;
    }

    const selfKey = getOrigKey(step);
    if (step.type === "LOOP" && loopsToShift.has(selfKey)) count++;

    return count;
  };

  const getShiftCountForStep = (step: any) => {
    if (step?.type === "LOOP_END") {
      const loopStart = loopStartByEndOrigId.get(getOrigKey(step));
      if (loopStart) {
        return getLoopShiftCount(loopStart);
      }
    }

    return getLoopShiftCount(step);
  };

  const maxBottomListCache = new WeakMap<any[], number>();
  const routeOffsetCache = new WeakMap<any, number[]>();

  const getStepBaseY = (step: any) => {
    if (typeof step?.iy === "number") return step.iy * GRID;
    if (typeof step?.position?.y === "number") return step.position.y;
    return 0;
  };

  const getStepVisualY = (step: any) =>
    getStepBaseY(step) +
    getShiftCountForStep(step) * NESTED_LOOP_VERTICAL_OFFSET;

  const getRouteStartY = (routerStep: any, route: any, routeIndex: number) => {
    const routeSteps = Array.isArray(route?.steps) ? route.steps : [];
    const firstStep = routeSteps[0];
    if (firstStep) return getStepVisualY(firstStep);

    return getStepVisualY(routerStep) + (routeIndex + 1) * GRID;
  };

  const getMaxBottomInList = (list: any[]): number => {
    if (!Array.isArray(list) || list.length === 0) return 0;
    const cached = maxBottomListCache.get(list);
    if (cached !== undefined) return cached;

    let maxBottom = Number.NEGATIVE_INFINITY;

    list.forEach((step) => {
      let stepBottom = getStepVisualY(step);

      if (
        step.type &&
        step.type !== "LOOP" &&
        step.type !== "LOOP_END" &&
        Array.isArray(step.routes)
      ) {
        const routeOffsets = getRouteOffsets(step);
        step.routes.forEach((route: any, routeIndex: number) => {
          const routeSteps = Array.isArray(route?.steps) ? route.steps : [];
          const routeBaseBottom =
            routeSteps.length > 0
              ? getMaxBottomInList(routeSteps)
              : getRouteStartY(step, route, routeIndex);
          const routeBottom = routeBaseBottom + (routeOffsets[routeIndex] || 0);
          if (routeBottom > stepBottom) stepBottom = routeBottom;
        });
      }

      if (stepBottom > maxBottom) maxBottom = stepBottom;
    });

    const normalizedBottom =
      maxBottom === Number.NEGATIVE_INFINITY ? 0 : maxBottom;
    maxBottomListCache.set(list, normalizedBottom);
    return normalizedBottom;
  };

  const getRouteOffsets = (routerStep: any): number[] => {
    if (!routerStep || !Array.isArray(routerStep.routes)) return [];
    const cached = routeOffsetCache.get(routerStep);
    if (cached) return cached;

    let previousBottom: number | null = null;
    const offsets = routerStep.routes.map((route: any, routeIndex: number) => {
      const routeSteps = Array.isArray(route?.steps) ? route.steps : [];
      const routeStartY = getRouteStartY(routerStep, route, routeIndex);
      const routeBaseBottom =
        routeSteps.length > 0 ? getMaxBottomInList(routeSteps) : routeStartY;
      let offset = 0;

      if (previousBottom !== null) {
        const requiredTop = previousBottom + GRID;
        offset = Math.max(0, requiredTop - routeStartY);
      }

      const routeBottom = routeBaseBottom + offset;
      previousBottom =
        previousBottom === null
          ? routeBottom
          : Math.max(previousBottom, routeBottom);

      return offset;
    });

    routeOffsetCache.set(routerStep, offsets);
    return offsets;
  };

  const apply = (list: any[], routeOffset = 0) => {
    list.forEach((step) => {
      const shiftCount = getShiftCountForStep(step);
      const extraY = shiftCount * NESTED_LOOP_VERTICAL_OFFSET;
      const ix = typeof step?.ix === "number" ? step.ix : 0;
      const iy = typeof step?.iy === "number" ? step.iy : 0;
      const baseY = iy * GRID + extraY + routeOffset;

      step.position = {
        x: ix * GRID,
        y: baseY,
      };

      if (step.type && step.type !== "LOOP" && step.type !== "LOOP_END") {
        const routeOffsets = getRouteOffsets(step);
        // eslint-disable-next-line
        step.routes?.forEach((r: any, routeIndex: number) =>
          apply(r.steps || [], routeOffset + (routeOffsets[routeIndex] || 0))
        );
      }
    });
  };

  const addLayoutJitter = (list: any[]) => {
    list.forEach((step) => {
      if (!step.type || step.type === "LOOP" || step.type === "LOOP_END") {
        step.position = {
          x: step.position.x + Math.random(),
          y: step.position.y + Math.random() / 10,
        };
      }

      if (step.type && step.type !== "LOOP" && step.type !== "LOOP_END") {
        // eslint-disable-next-line
        step.routes?.forEach((r: any) => addLayoutJitter(r.steps || []));
      }
    });
  };

  apply(layoutRootSteps);
  if (options.applySpacing !== false) {
    compactOrganizedSpacingByOrder(
      layoutRootSteps,
      STRUCTURE_HORIZONTAL_GAP,
      options.movableOrigIds
    );
  }
  addLayoutJitter(layoutRootSteps);
}
